import { type RefObject, useEffect } from 'react'
import { goPublic, resolvePublicRoute } from '../lib/public-router'

/**
 * Follow a link to another page of this site in place, rather than fetching the
 * document again.
 *
 * Every public shell shares this, because a site where some links reload and
 * others do not is worse than one where they all do: the masthead's own picker
 * crosses from a congregation's site to the directory, and a visitor moving
 * between them would meet a blank frame at whichever step happened to be the
 * one nobody wired up.
 *
 * The links stay real `<a href>`s — right-click, open-in-new-tab, a pasted
 * address and a crawler all need the destination to exist without a click — and
 * only the ordinary click is answered here. Anything the browser would do
 * differently is left to the browser: a modified or middle click opens a tab, a
 * target names another frame, a download is a download, and an address off this
 * site is not ours to intercept.
 *
 * It attaches a listener rather than taking an `onClick`, because an element
 * wearing a click handler is a control by every rule the linter has, and this is
 * not a control — it is the site answering for its own links. Keyboard operation
 * is unaffected: Enter on a link raises the same click, and `goPublic` puts the
 * reader at the start of the page it opened.
 *
 * @param surface The shell's outermost element. It holds the page as well as the
 * masthead and footer, so a link a PAGE renders is followed in place too — which
 * is what makes this the site's answer for its links rather than the chrome's.
 */
export function useFollowInPlace(surface: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = surface.current
    if (!el) return
    const follow = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

      const link = (e.target as Element | null)?.closest('a')
      // An SVG `<a>` also answers `closest('a')`, and its `href` is an
      // `SVGAnimatedString` rather than a string — so the kind is tested rather
      // than inferred, and such a link navigates the ordinary way.
      if (!(link instanceof HTMLAnchorElement)) return
      // An anchor with no `href` is not a link at all: it is a control somebody
      // wrote as an `<a>`, and it has its own handler. `href="#"` is the same
      // thing spelled differently — both resolve to this very page, which is
      // why the fragment guard below cannot catch them.
      const attr = link.getAttribute('href')
      if (!attr || attr === '#') return
      if (link.target && link.target !== '_self') return
      if (link.hasAttribute('download')) return

      // A malformed href is the browser's to refuse, not ours to throw on.
      let url: URL
      try {
        url = new URL(link.href, window.location.href)
      } catch {
        return
      }

      // Another of our sites is a different document on a different host, so it
      // is a real navigation however much it looks like one of ours. Every
      // opaque scheme — `mailto:`, `tel:` — has an origin of "null" and leaves
      // here too, which is what the footer's own contact links depend on.
      if (url.origin !== window.location.origin) return

      const here = window.location
      // A fragment on the page already open is the browser's to answer: it
      // scrolls to the element and leaves the history entry the section's own.
      // Treated as a page change it would scroll to the top instead, which is
      // the marketing masthead taking you away from what you clicked.
      if (
        url.hash &&
        url.pathname === here.pathname &&
        url.search === here.search
      )
        return

      // An address this bundle cannot resolve belongs to the server, which is
      // what serves a file or a route the app does not hold.
      if (resolvePublicRoute(url).kind === 'home' && url.pathname !== '/')
        return

      e.preventDefault()
      goPublic(`${url.pathname}${url.search}${url.hash}`)
    }
    el.addEventListener('click', follow)
    return () => el.removeEventListener('click', follow)
  }, [surface])
}
